import type { Game } from '../engine/game';
import { SHAPES, boxSize } from '../engine/pieces';
import type { ActivePiece } from '../engine/types';

// LCD-Farbrollen (entsprechen den CSS-Variablen).
const INK = '#0f380f';
const BG = '#8bac0f';
const FRAME = '#9bbc0f';
const GHOST = '#306230';

const FILLED_SHADOW = `inset 0 0 0 1px ${BG}, inset 0 0 0 2px ${INK}`;
const GHOST_SHADOW = `inset 0 0 0 1px ${GHOST}`;

type CellValue = 0 | 1 | 2; // leer | gefüllt | ghost

interface CellStyle {
  bg: string;
  shadow: string;
}

function styleFor(v: CellValue): CellStyle {
  if (v === 1) return { bg: INK, shadow: FILLED_SHADOW };
  if (v === 2) return { bg: BG, shadow: GHOST_SHADOW };
  return { bg: BG, shadow: 'none' };
}

/**
 * Rendert Spielfeld, NEXT-Vorschau und Statuswerte in ein bestehendes DOM.
 * Kein Spiellogik-Zustand — nur Darstellung.
 */
export class Display {
  private readonly cells: HTMLDivElement[] = [];
  private readonly nextCells: HTMLDivElement[] = [];
  private readonly lastCell: string[] = [];
  private readonly lastNext: string[] = [];

  constructor(
    private readonly playfield: HTMLElement,
    private readonly nextGrid: HTMLElement,
    private readonly valScore: HTMLElement,
    private readonly valLevel: HTMLElement,
    private readonly valLines: HTMLElement,
    private readonly cols: number,
    private readonly rows: number,
  ) {
    this.buildGrid();
  }

  private buildGrid(): void {
    const frag = document.createDocumentFragment();
    for (let i = 0; i < this.cols * this.rows; i++) {
      const d = document.createElement('div');
      d.className = 'cell';
      this.cells.push(d);
      this.lastCell.push('');
      frag.appendChild(d);
    }
    this.playfield.appendChild(frag);

    const nfrag = document.createDocumentFragment();
    for (let i = 0; i < 16; i++) {
      const d = document.createElement('div');
      d.className = 'next-cell';
      this.nextCells.push(d);
      this.lastNext.push('');
      nfrag.appendChild(d);
    }
    this.nextGrid.appendChild(nfrag);
  }

  render(game: Game, showGhost: boolean): void {
    const grid = this.composeGrid(game, showGhost);

    for (let i = 0; i < grid.length; i++) {
      const s = styleFor(grid[i]);
      const key = s.bg + '|' + s.shadow;
      if (this.lastCell[i] !== key) {
        const el = this.cells[i];
        el.style.background = s.bg;
        el.style.boxShadow = s.shadow;
        this.lastCell[i] = key;
      }
    }

    this.renderNext(game.nextType);

    this.valScore.textContent = String(game.score).padStart(5, '0');
    this.valLevel.textContent = String(game.level);
    this.valLines.textContent = String(game.lines).padStart(3, '0');
  }

  private composeGrid(game: Game, showGhost: boolean): CellValue[] {
    const { cols, rows } = this;
    const grid: CellValue[] = new Array(cols * rows).fill(0);
    const board = game.board;

    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (board[y][x]) grid[y * cols + x] = 1;
      }
    }

    if (showGhost && game.ghost && game.active && game.ghost.y !== game.active.y) {
      for (const [x, y] of this.absCells(game.ghost)) {
        if (y >= 0 && y < rows && x >= 0 && x < cols && grid[y * cols + x] === 0) {
          grid[y * cols + x] = 2;
        }
      }
    }

    if (game.active) {
      for (const [x, y] of this.absCells(game.active)) {
        if (y >= 0 && y < rows && x >= 0 && x < cols) grid[y * cols + x] = 1;
      }
    }

    return grid;
  }

  private absCells(piece: ActivePiece): Array<[number, number]> {
    return SHAPES[piece.type][piece.rotation].map(
      ([dx, dy]) => [piece.x + dx, piece.y + dy] as [number, number],
    );
  }

  private renderNext(type: Game['nextType']): void {
    // NEXT-Stein oben-links in der 4×4-Box, in Spawn-Orientierung.
    const shape = SHAPES[type][0];
    const size = boxSize(type);
    const filled = new Set(shape.map(([x, y]) => y * 4 + x));
    // Für 2×2 (O) und 3×3 passt die lokale (x,y) direkt in die 4er-Zeile.
    void size;
    for (let i = 0; i < 16; i++) {
      const on = filled.has(i);
      const key = on ? '1' : '0';
      if (this.lastNext[i] !== key) {
        const el = this.nextCells[i];
        if (on) {
          el.style.background = INK;
          el.style.boxShadow = FILLED_SHADOW;
        } else {
          el.style.background = FRAME;
          el.style.boxShadow = 'none';
        }
        this.lastNext[i] = key;
      }
    }
  }
}
